import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import * as AnalyticsService from '../../services/analytics'
import { useAnalyticsFilters } from '../../contexts/AnalyticsFilterContext'
import AnalyticsPageShell from './components/AnalyticsPageShell'
import { ClickableRow, SimpleTable, TableWrap } from './styles'
import { formatNumber, getAnalyticsErrorMessage } from './utils'

const Workshops = () => {
    const history = useHistory()
    const { rangeFilters } = useAnalyticsFilters()
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [rows, setRows] = useState([])
    const [sortKey, setSortKey] = useState('serviceOrders')
    const [sortDir, setSortDir] = useState('desc')

    useEffect(() => {
        let cancelled = false

        async function load() {
            setLoading(true)
            setError(null)
            try {
                const data = await AnalyticsService.getWorkshops(rangeFilters)
                if (!cancelled) setRows(Array.isArray(data) ? data : [])
            } catch (err) {
                if (!cancelled) setError(getAnalyticsErrorMessage(err))
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        load()
        return () => {
            cancelled = true
        }
    }, [rangeFilters])

    const sorted = useMemo(() => {
        const copy = [...rows]
        copy.sort((a, b) => {
            const av = a[sortKey]
            const bv = b[sortKey]
            if (typeof av === 'string') {
                return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av)
            }
            return sortDir === 'asc' ? av - bv : bv - av
        })
        return copy
    }, [rows, sortKey, sortDir])

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))
        } else {
            setSortKey(key)
            setSortDir('desc')
        }
    }

    const header = (key, label) => (
        <th style={{ cursor: 'pointer' }} onClick={() => toggleSort(key)}>
            {label}
            {sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
        </th>
    )

    return (
        <AnalyticsPageShell title='Análises de negócio — Oficinas' loading={loading} error={error} showWorkshopId>
            <TableWrap>
                {!sorted.length ? (
                    <div style={{ padding: 16, color: 'var(--text-tertiary)' }}>Nenhuma oficina no período.</div>
                ) : (
                    <SimpleTable>
                        <thead>
                            <tr>
                                {header('workshopName', 'Oficina')}
                                {header('serviceOrders', 'OSs')}
                                {header('remindersCompleted', 'Lembretes concluídos')}
                                {header('maintenanceEvents', 'Manutenções')}
                                {header('campaignsSent', 'Campanhas')}
                                {header('uniqueCustomers', 'Clientes únicos')}
                                {header('repeatCustomers', 'Clientes recorrentes')}
                            </tr>
                        </thead>
                        <tbody>
                            {sorted.map((row) => (
                                <ClickableRow
                                    key={row.workshopId}
                                    onClick={() => history.push(`/dashboard/analytics/workshops/${row.workshopId}`)}
                                >
                                    <td>
                                        <div style={{ fontWeight: 600 }}>{row.workshopName}</div>
                                        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                                            ID {row.workshopId}
                                        </div>
                                    </td>
                                    <td>{formatNumber(row.serviceOrders)}</td>
                                    <td>{formatNumber(row.remindersCompleted)}</td>
                                    <td>{formatNumber(row.maintenanceEvents)}</td>
                                    <td>{formatNumber(row.campaignsSent)}</td>
                                    <td>{formatNumber(row.uniqueCustomers)}</td>
                                    <td>{formatNumber(row.repeatCustomers)}</td>
                                </ClickableRow>
                            ))}
                        </tbody>
                    </SimpleTable>
                )}
            </TableWrap>
        </AnalyticsPageShell>
    )
}

export default Workshops
